import { DurationUnit } from "luxon";

import { Gender } from "@/Common/Models/Patient/PatientEnums";
import { VitalSignPatientData } from "@/Common/Models/VitalSignPatientData";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { VitalProperty } from "@/Patient/Models/GrowthCharts/BaseChartModel";
import { GrowthChartsCacheData } from "@/Patient/Models/GrowthCharts/GrowthChartsCacheData";
import { CurvesName } from "@/Patient/Models/GrowthCharts/LoadGrowthCurvesCacheResponse";
import { MedicalItemVitalSign } from "@/Patient/Models/MedicalSummary/MedicalItemVitalSign";
import { VitalSign } from "@/Patient/Models/VitalSigns/VitalSign";
import { VitalSignModelConverter } from "@/Patient/Models/VitalSigns/VitalSignModelConverter";
import { CorrectedAgeCalculator } from "@/Patient/Services/GrowthCharts/Calculators/CorrectedAgeCalculator";
import { CurveNameGetter } from "@/Patient/Services/GrowthCharts/Calculators/CurveNameGetter";
import { PercentileCalculator } from "@/Patient/Services/GrowthCharts/Calculators/PercentileCalculator";

export class VitalSignPercentileUtility {
    private percentileCalculator: PercentileCalculator;
    private gender: Gender;
    private dateOfBirth: Date;
    private bornPrematureWeeks: number;
    private isPremature: boolean;
    private isCurrentSelectionFenton: boolean;

    public constructor(growthChartsCache: GrowthChartsCacheData, patientDate?: VitalSignPatientData) {
        this.percentileCalculator = new PercentileCalculator(
            growthChartsCache.Interpolations,
            growthChartsCache.ZTable
        );
        this.gender = patientDate.gender;
        this.dateOfBirth = patientDate.dateOfBirth;
        this.isPremature = patientDate.isPremature;
        this.bornPrematureWeeks = patientDate.bornPrematureWeeks;
        this.isCurrentSelectionFenton = patientDate.isFenton;
    }

    public calculatePercentile(property: VitalProperty, vitalSign: VitalSign): string | null {
        const medicalItemVitalSign = VitalSignModelConverter.convertToMedicalItem(vitalSign);

        return this.calculatePercentileFromMedicalItemVitalSign(property, medicalItemVitalSign);
    }

    public calculatePercentileFromMedicalItemVitalSign(property: VitalProperty, medicalItemVitalSign: MedicalItemVitalSign): string | null {
        if (!medicalItemVitalSign?.PharmaStartDate || !medicalItemVitalSign[property])
            return null;

        let isFentonChart = false;
        let dateOfBirth = this.dateOfBirth;
        if (this.isCurrentSelectionFenton) {
            isFentonChart = true;
        }
        else {
            if (this.isPremature && !!this.bornPrematureWeeks) {
                const correctedDate = CorrectedAgeCalculator.calculateCorrectedAge(this.dateOfBirth, this.bornPrematureWeeks);
                const vitalSignCorrectedAge = this.getDateDiff("months", correctedDate, medicalItemVitalSign.PharmaStartDate);
                const correctedAge = this.getDateDiff("months", correctedDate, new Date());
                if (vitalSignCorrectedAge < 0)
                    isFentonChart = true;
                else if (correctedAge <= 24)
                    dateOfBirth = correctedDate;
            }
        }

        const age = this.getDateDiff("years", this.dateOfBirth, medicalItemVitalSign.PharmaStartDate);
        const chartName: CurvesName = CurveNameGetter.getChartFor(age, property, this.gender, isFentonChart);

        if (!chartName || !medicalItemVitalSign?.PharmaStartDate || (medicalItemVitalSign[property] === null || medicalItemVitalSign[property] === undefined))
            return null;

        let percentile = this.percentileCalculator.getPercentile(
            dateOfBirth,
            medicalItemVitalSign,
            property,
            chartName,
            chartName?.contains("fenton") && property === "Weight",
            this.bornPrematureWeeks
        );

        return percentile?.toString();
    }

    public getPercentileText(property: VitalProperty, percentiles: { [index in VitalProperty]?: string }): React.ReactNode {
        let stringToRender = percentiles?.[property];

        if (IsNullOrWhiteSpace(stringToRender))
            return null;

        stringToRender = `${stringToRender} %`;

        // Only show corrected string if patient is under 2 years and is premature.
        if (this.isPremature && this.getCorrectedAge() < 2)
            stringToRender = `${stringToRender} ${cacheStore.GetString("PercentileCorrectedLabel")}`;

        return `(${stringToRender})`;
    }

    public getAllCalculatedPercentiles(vitalSign: VitalSign): { [index in VitalProperty]?: string } {
        return {
            "Height": this.calculatePercentile("Height", vitalSign),
            "Weight": this.calculatePercentile("Weight", vitalSign),
            "HeadCircumference": this.calculatePercentile("HeadCircumference", vitalSign),
            "MassBodyIndex": this.calculatePercentile("MassBodyIndex", vitalSign)
        };
    }

    public getAllCalculatedPercentilesForMedicalItemVitalSign(vitalSign: MedicalItemVitalSign): { [index in VitalProperty]?: string } {
        return {
            "Height": this.calculatePercentileFromMedicalItemVitalSign("Height", vitalSign),
            "Weight": this.calculatePercentileFromMedicalItemVitalSign("Weight", vitalSign),
            "HeadCircumference": this.calculatePercentileFromMedicalItemVitalSign("HeadCircumference", vitalSign),
            "MassBodyIndex": this.calculatePercentileFromMedicalItemVitalSign("MassBodyIndex", vitalSign)
        };
    }

    public getAgeFromPatientDateOfBirthAndPharmaStartDateDifference(patientDateOfBirth: Date, pharmaStartDate: Date): number {
        const patientCurrentAge = LuxonDateTimeParser.parse(patientDateOfBirth)?.diffNow(["years"]).years * -1;
        const numberOfYearsFromPharmaStartDate = LuxonDateTimeParser.parse(pharmaStartDate)?.diffNow(["years"]).years * -1;

        return patientCurrentAge - numberOfYearsFromPharmaStartDate;
    }

    public getDateDiff(timeUnit: DurationUnit, startDate: Date, endDate: Date): number | null {
        if (!startDate || !endDate)
            return null;

        const luxonStartDate = LuxonDateTimeParser
            .parse(startDate)
            .set({ hour: 0 })
            .set({ minute: 0 })
            .set({ second: 0 })
            .set({ millisecond: 0 });
        const luxonEndDate = LuxonDateTimeParser
            .parse(endDate)
            .set({ hour: 0 })
            .set({ minute: 0 })
            .set({ second: 0 })
            .set({ millisecond: 0 });

        const diffData = luxonEndDate.diff(luxonStartDate, [timeUnit]);
        return diffData[timeUnit];
    }

    private getCorrectedAge(): number | null {
        const correctedAgeDate = CorrectedAgeCalculator.calculateCorrectedAge(this.dateOfBirth, this.bornPrematureWeeks ?? 40);
        return LuxonDateTimeParser.parse(correctedAgeDate)?.diffNow(["years"]).years * -1;
    }
}
